import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import { gql, useQuery, useMutation } from "@apollo/client";
import ConfirmModal from "../../components/modals/ConfirmModal";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import EntityOperationBar from "../../components/EntityOperationBar";
import JobSchedulesCalendarView from "./JobSchedulesCalendarView";
import JobSchedulesListingView from "./JobSchedulesListingView";
import JobScheduleSidebar from "./JobScheduleSidebar";
import StarekContext from "../../context/StarekContext";
import { usePermissions } from "../../hooks/usePermissions";
import usePagedList, { sortToOrderBy } from "../../hooks/usePagedList";
import Paginator from "../../components/Paginator";

const UI_STRINGS = {
    PAGE_TITLE: "Job Schedules",
    ENTITY_SINGULAR: "Job Schedule",
    ENTITY_PLURAL: "Job Schedules",
    DELETE_TITLE_SINGLE: (name) => `Delete Job Schedule ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Job Schedules?`,
    DELETE_MESSAGE_SINGLE: (
        name,
    ) => `You are about to delete job schedule "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} job schedule${count !== 1 ? "s" : ""}.

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total job schedules selected: ${count}`,
    NOT_FOUND: "Job schedule not found",
};

// Everything the calendar, the table and the sidebar read off a job schedule.
const JOB_SCHEDULE_FIELDS = `
    id
    name
    created_at
    updated_at
    created_by {
        id
        name
    }
    updated_by {
        id
        name
    }
    description
    part_id
    part {
        id
        name
        plant_id
        description
        is_active
    }
    device_id
    completed_cycles
    target_units
    operations {
        id
        operation_id
        sequence_no
        status
        planned_duration
        incremental_weight
        decremental_weight
        absolute_weight
        device_id
        operation {
            id
            name
            is_productive
        }
    }
    shift_id
    shift {
        id
        name
        start_time
        end_time
    }
    end_time
    start_time
    plant_id
    plant {
        name
        id
    }
`;

/**
 * Table sort -> Prisma orderBy. The Part, Shift and Progress columns sort through a
 * relation (part name, shift name, number of route steps), which sortToOrderBy's flat
 * { field: direction } cannot express; everything else is a plain column.
 */
const jobScheduleOrderBy = (sort) => {
    if (!sort?.field) return [];
    const direction = sort.direction === "desc" ? "desc" : "asc";
    switch (sort.field) {
        case "part":
            return [{ part: { name: direction } }];
        case "shift":
            return [{ shift: { name: direction } }];
        case "operations":
            return [{ operations: { _count: direction } }];
        default:
            return sortToOrderBy(sort, ["description", "device_id"]);
    }
};

const JobSchedules = (props) => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeJobId = parseRouteEntityId(routeId);
    const { can } = usePermissions();
    const canWrite   = can("job_schedule", "WRITE");
    const canDestroy = can("job_schedule", "DESTROY");
    const [searchParams, setSearchParams] = useSearchParams();
    // Read the view straight from the URL on the first render, so a ?view=list link
    // does not fire the calendar's full query first.
    const [viewMode, setViewMode] = useState(() =>
        searchParams.get("view") === "list" ? "list" : "calendar",
    );
    const isListView = viewMode === "list";
    const [selectedRows, setSelectedRows] = useState([]);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "calendar" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [filterText, setFilterText] = useState("");
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [routeEditJob, setRouteEditJob] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    // Ids just deleted: the refresh drops them while the route may still point at one.
    const deletedIdsRef = useRef(new Set());

    // Every job schedule of the plant, for the calendar view.
    const GET_JOB_SCHEDULES = gql`
        query GetJobSchedules($plantId: Int!) {
            jobSchedules(where: { plant_id: { equals: $plantId } }) {
                ${JOB_SCHEDULE_FIELDS}
            }
        }
    `;

    // One page of job schedules plus the total, for the list view (usePagedList).
    const GET_JOB_SCHEDULES_PAGE = gql`
        query GetJobSchedulesPage(
            $where: JobScheduleWhereInput!
            $orderBy: [JobScheduleOrderByWithRelationInput!]
            $take: Int
            $skip: Int
        ) {
            jobSchedules(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
                ${JOB_SCHEDULE_FIELDS}
            }
            aggregateJobSchedule(where: $where) {
                _count {
                    _all
                }
            }
        }
    `;

    // A deep link (/job-schedules/:id) may point at a run that is not on the current
    // page. findFirst goes through the soft-delete filter, so an archived run is not found.
    const GET_JOB_SCHEDULE_BY_ID = gql`
        query GetJobScheduleById($where: JobScheduleWhereInput!) {
            findFirstJobSchedule(where: $where) {
                ${JOB_SCHEDULE_FIELDS}
            }
        }
    `;

    const CREATE_ONE_JOB_SCHEDULE = gql`
        mutation CreateOneJobSchedule($data: JobScheduleCreateInput!) {
            createOneJobSchedule(data: $data) {
                id
                name
                description
            }
        }
    `;

    const UPDATE_ONE_JOB_SCHEDULE = gql`
        mutation UpdateOneJobSchedule(
            $data: JobScheduleUpdateInput!
            $where: JobScheduleWhereUniqueInput!
        ) {
            updateOneJobSchedule(data: $data, where: $where) {
                id
                name
                description
            }
        }
    `;

    const DELETE_ONE_JOB_SCHEDULE = gql`
        mutation DeleteOneJobSchedule($id: Int!) {
            deleteOneJobSchedule(where: { id: $id }) {
                id
            }
        }
    `;

    // Calendar view: every run of the plant. Only run while the calendar is showing —
    // the list view reads its own page below.
    // network-only: coming back from the list view must not replay a calendar cached
    // before the edits made there.
    const {
        data,
        previousData,
        loading: fullLoading,
        error: fullError,
        refetch: refetchFull,
    } = useQuery(GET_JOB_SCHEDULES, {
        client: starekContext.engineClient,
        skip: isListView,
        variables: { plantId: starekContext.lastUsedPlant.id },
        fetchPolicy: "network-only",
        nextFetchPolicy: "network-only",
    });
    const fullShown = data ?? previousData;

    // List view: table sort, applied on the server ({ field, direction } or null).
    const [sort, setSort] = useState(null);
    const orderBy = useMemo(() => jobScheduleOrderBy(sort), [sort]);
    const jobScheduleWhere = useMemo(
        () => ({ plant_id: { equals: starekContext.lastUsedPlant.id } }),
        [starekContext.lastUsedPlant.id],
    );

    // List view: one page (12) of runs from the server, searched and sorted there.
    const {
        items: pageItems,
        total,
        page,
        pageCount,
        pageSize,
        setPage,
        loading: pageLoading,
        initialLoading: pageInitialLoading,
        error: pageError,
        refetch: refetchPage,
    } = usePagedList({
        query: GET_JOB_SCHEDULES_PAGE,
        listField: "jobSchedules",
        countField: "aggregateJobSchedule",
        where: jobScheduleWhere,
        searchText: filterText,
        orderBy,
        client: starekContext.engineClient,
        skip: !isListView,
    });

    // /job-schedules/:id may point at a run that is not on this page, so it is loaded
    // on its own.
    const {
        data: routeJobData,
        loading: routeJobLoading,
        refetch: refetchRouteJob,
    } = useQuery(GET_JOB_SCHEDULE_BY_ID, {
        client: starekContext.engineClient,
        skip: routeJobId == null,
        variables: {
            where: {
                id: { equals: routeJobId },
                plant_id: { equals: starekContext.lastUsedPlant.id },
            },
        },
        fetchPolicy: "network-only",
    });

    const ADVANCE_ACTIVE_SCHEDULES = gql`
        mutation AdvanceActiveJobSchedules($plantId: Int!) {
            advanceActiveJobSchedules(plant_id: $plantId) {
                job_schedule_id
                completed_operations
                total_operations
                completed_cycles
                cycle_completed_now
            }
        }
    `;

    const DELETE_MANY_JOB_SCHEDULE_OPERATION = gql`
        mutation DeleteManyJobScheduleOperation($ids: [Int!]!) {
            deleteManyJobScheduleOperation(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const UPDATE_ONE_JOB_SCHEDULE_OPERATION = gql`
        mutation UpdateOneJobScheduleOperation(
            $data: JobScheduleOperationUpdateInput!
            $where: JobScheduleOperationWhereUniqueInput!
        ) {
            updateOneJobScheduleOperation(data: $data, where: $where) {
                id
                sequence_no
                status
            }
        }
    `;

    const CREATE_ONE_JOB_SCHEDULE_OPERATION = gql`
        mutation CreateOneJobScheduleOperation(
            $data: JobScheduleOperationCreateInput!
        ) {
            createOneJobScheduleOperation(data: $data) {
                id
            }
        }
    `;

    const [advanceActiveSchedules] = useMutation(ADVANCE_ACTIVE_SCHEDULES, {
        client: starekContext.engineClient,
    });

    const [deleteManyJobScheduleOperation] = useMutation(
        DELETE_MANY_JOB_SCHEDULE_OPERATION,
        { client: starekContext.engineClient },
    );
    const [updateOneJobScheduleOperation] = useMutation(
        UPDATE_ONE_JOB_SCHEDULE_OPERATION,
        { client: starekContext.engineClient },
    );
    const [createOneJobScheduleOperation] = useMutation(
        CREATE_ONE_JOB_SCHEDULE_OPERATION,
        { client: starekContext.engineClient },
    );

    const [createJobSchedule] = useMutation(CREATE_ONE_JOB_SCHEDULE, {
        client: starekContext.engineClient,
    });
    const [updateJobSchedule] = useMutation(UPDATE_ONE_JOB_SCHEDULE, {
        client: starekContext.engineClient,
    });
    const [deleteOneJobSchedule] = useMutation(DELETE_ONE_JOB_SCHEDULE, {
        client: starekContext.engineClient,
    });

    const jobSchedules = useMemo(() => fullShown?.jobSchedules ?? [], [fullShown]);
    // Rows the active view is showing — the route effect prefers these (fresh after
    // every refresh) over the by-id copy.
    const activeRows = isListView ? pageItems : jobSchedules;
    const error = isListView ? pageError : fullError;

    // Selection is per page: moving pages clears it.
    useEffect(() => {
        setSelectedRows([]);
        setToggleCleared((p) => !p);
    }, [page]);

    /**
     * Re-reads what the active view shows after a write (only that view's query is
     * running), plus the open run when it is not among those rows so the sidebar sees
     * the write too. A just-deleted run is skipped — it would read as "not found".
     */
    // After any write, re-read the open record too (unless it was just deleted): the
    // page rows from before the refetch cannot tell whether it is still on the page, and
    // updateMany returns only a count, so the cached by-id copy would otherwise go stale.
    const hardRefresh = async () => {
        try {
            await (isListView ? refetchPage() : refetchFull());
            if (
                routeJobId != null &&
                !deletedIdsRef.current.has(routeJobId)
            ) {
                await refetchRouteJob();
            }
        } catch (err) {
            console.error("Failed to refresh job schedules:", err);
        }
    };

    /**
     * Bring every started-but-unfinished run up to date from telemetry when the screen
     * opens. Without this a run only moves when somebody opens it, so a line could
     * produce all week with the cycle count sitting at zero.
     */
    const hardRefreshRef = useRef(hardRefresh);
    hardRefreshRef.current = hardRefresh;

    useEffect(() => {
        const plantId = starekContext.lastUsedPlant?.id;
        if (!plantId) return;
        let cancelled = false;
        advanceActiveSchedules({ variables: { plantId } })
            .then(({ data: advanced }) => {
                if (cancelled) return;
                const moved = (advanced?.advanceActiveJobSchedules || []).filter(
                    (run) => run.cycle_completed_now,
                );
                if (moved.length) {
                    starekContext.showSuccess(
                        `${moved.length} cycle${moved.length === 1 ? "" : "s"} completed from machine activity`,
                    );
                }
                // Latest hardRefresh: the view may have switched (calendar <-> list)
                // while the advance was in flight, which changes the query to refresh.
                hardRefreshRef.current();
            })
            .catch(() => {
                // Telemetry is best-effort; the manual controls remain the fallback.
            });
        return () => {
            cancelled = true;
        };
    }, [starekContext.lastUsedPlant?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!routeId) {
            setRouteEditJob(null);
            return;
        }

        if (routeJobLoading) return;

        const listRoute = buildListRoute("/job-schedules", searchParams);

        if (routeJobId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditJob(null);
            navigate(listRoute, { replace: true });
            return;
        }

        // Just deleted from the sidebar: the sidebar closes itself. Checked before the
        // lookup, because the by-id result can still hold the deleted row for a moment.
        if (deletedIdsRef.current.has(routeJobId)) return;

        // Prefer the row the active view shows; otherwise the run loaded by id.
        const jobToEditRecord =
            activeRows.find((job) => Number(job?.id) === routeJobId) ||
            routeJobData?.findFirstJobSchedule;

        if (!jobToEditRecord) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditJob(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditJob(jobToEditRecord);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeJobId
                ? current
                : [jobToEditRecord],
        );
        setShowSidebar(true);
    }, [
        routeId,
        routeJobId,
        routeJobLoading,
        routeJobData,
        activeRows,
        searchParams,
        navigate,
        starekContext,
    ]);

    // The full-page loader is for the page's first load only (see the render below).
    const activeInitialLoading = isListView
        ? pageInitialLoading
        : fullLoading && !fullShown;
    const [hasLoaded, setHasLoaded] = useState(false);
    useEffect(() => {
        if (!activeInitialLoading) setHasLoaded(true);
    }, [activeInitialLoading]);

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/job-schedules/${row.id}`, searchParams));
    };

    const handleAdd = () => {
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(buildListRoute(`/job-schedules/${selectedRows[0].id}`, searchParams));
    };

    const handleEditJob = (job) => {
        navigate(buildListRoute(`/job-schedules/${job.id}`, searchParams));
    };

    const handleSidebarClose = async () => {
        setShowSidebar(false);
        setRouteEditJob(null);
        if (routeId) {
            navigate(buildListRoute("/job-schedules", searchParams), {
                replace: true,
            });
        }
        await hardRefresh();
        setToggleCleared((p) => !p);
        setSelectedRows([]);
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveJobSchedule = async (jobPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id ||
                2;

            // The Part's operation sequence, snapshotted onto this run as child rows.
            // The three score weights ride along as a snapshot of the route.
            const weightScalars = (op) => ({
                incremental_weight: Number(op.incremental_weight) || 0,
                decremental_weight: Number(op.decremental_weight) || 0,
                absolute_weight:
                    op.absolute_weight === null || op.absolute_weight === undefined
                        ? null
                        : Number(op.absolute_weight),
            });

            const buildOperationsCreate = (operations = []) =>
                operations.map((op) => ({
                    sequence_no: op.sequence_no,
                    planned_duration: op.planned_duration ?? null,
                    ...weightScalars(op),
                    operation: { connect: { id: Number(op.operation_id) } },
                    ...(op.device_id
                        ? { device: { connect: { id: op.device_id } } }
                        : {}),
                }));

            // Handed back to the sidebar so its next step can deep-link this run.
            let savedJobId = jobPayload.id ?? null;

            if (!jobPayload.id) {
                // Create
                const { data: created } = await createJobSchedule({
                    variables: {
                        data: {
                            name: jobPayload.name,
                            description: jobPayload.description || "",
                            start_time: jobPayload.start_time,
                            end_time: jobPayload.end_time,
                            target_units: jobPayload.target_units ?? null,
                            ...(jobPayload.shift_id
                                ? {
                                      shift: {
                                          connect: { id: jobPayload.shift_id },
                                      },
                                  }
                                : {}),
                            plant: {
                                connect: { id: starekContext.lastUsedPlant.id },
                            },
                            part: {
                                connect: { id: Number(jobPayload.part_id) },
                            },
                            device: {
                                connect: { id: jobPayload.device_id },
                            },
                            operations: {
                                create: buildOperationsCreate(
                                    jobPayload.operations,
                                ),
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
                savedJobId = created?.createOneJobSchedule?.id ?? null;
            } else {
                // Reconcile the route steps rather than replacing them. Each step
                // carries run progress now — status, start and end stamps — and
                // recreating the rows would reset a part-finished run to Pending and
                // let its cycle be counted a second time.
                // The run being edited is the routed one: on the active view's rows,
                // or loaded by id when it is on another page.
                const isEdited = (job) =>
                    Number(job?.id) === Number(jobPayload.id);
                const existingOperations =
                    (
                        activeRows.find(isEdited) ||
                        (isEdited(routeJobData?.findFirstJobSchedule)
                            ? routeJobData.findFirstJobSchedule
                            : null) ||
                        (isEdited(routeEditJob) ? routeEditJob : null)
                    )?.operations || [];

                const submitted = jobPayload.operations || [];
                const keptIds = new Set(
                    submitted
                        .filter((op) => op.id)
                        .map((op) => Number(op.id)),
                );

                const removedIds = existingOperations
                    .map((op) => Number(op.id))
                    .filter((id) => !keptIds.has(id));

                if (removedIds.length) {
                    await deleteManyJobScheduleOperation({
                        variables: { ids: removedIds },
                    });
                }

                for (const op of submitted) {
                    const shared = {
                        sequence_no: op.sequence_no,
                        planned_duration: op.planned_duration ?? null,
                        ...weightScalars(op),
                    };

                    if (op.id) {
                        // Weights are not re-sent on update: the snapshot taken
                        // when the step was scheduled is the one the run is
                        // scored by.
                        await updateOneJobScheduleOperation({
                            variables: {
                                where: { id: Number(op.id) },
                                data: {
                                    sequence_no: { set: shared.sequence_no },
                                    planned_duration: {
                                        set: shared.planned_duration,
                                    },
                                    operation: {
                                        connect: {
                                            id: Number(op.operation_id),
                                        },
                                    },
                                    device: op.device_id
                                        ? { connect: { id: op.device_id } }
                                        : { disconnect: true },
                                },
                            },
                        });
                    } else {
                        await createOneJobScheduleOperation({
                            variables: {
                                data: {
                                    ...shared,
                                    job_schedule: {
                                        connect: { id: Number(jobPayload.id) },
                                    },
                                    operation: {
                                        connect: {
                                            id: Number(op.operation_id),
                                        },
                                    },
                                    ...(op.device_id
                                        ? {
                                              device: {
                                                  connect: { id: op.device_id },
                                              },
                                          }
                                        : {}),
                                },
                            },
                        });
                    }
                }

                await updateJobSchedule({
                    variables: {
                        where: { id: jobPayload.id },
                        data: {
                            name: { set: jobPayload.name },
                            description: { set: jobPayload.description || "" },
                            start_time: { set: jobPayload.start_time },
                            end_time: { set: jobPayload.end_time },
                            target_units: { set: jobPayload.target_units ?? null },
                            shift: jobPayload.shift_id
                                ? { connect: { id: jobPayload.shift_id } }
                                : { disconnect: true },
                            part: {
                                connect: { id: Number(jobPayload.part_id) },
                            },
                            device: {
                                connect: { id: jobPayload.device_id },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            }

            // The sidebar stays open to confirm the save and point at the next screen;
            // it closes when the user picks a next step or presses Done.
            await hardRefresh();
            starekContext.showSuccess(
                jobPayload.id
                    ? `Job Schedule "${jobPayload.name}" updated successfully`
                    : `Job Schedule "${jobPayload.name}" created successfully`,
            );
            return { id: savedJobId };
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    // Toolbar delete and sidebar delete both confirm here. `onSettled(ok)` lets the
    // sidebar stay open until the dialog is answered (true only after a full delete).
    const handleDelete = (jobs, onSettled) => {
        if (!jobs || jobs.length === 0 || isDeleting) {
            onSettled?.(false);
            return;
        }

        const isSingle = jobs.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(jobs[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(jobs.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(jobs[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(jobs.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(jobs.length)}
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);
                let failed = 0;

                try {
                    for (const job of jobs) {
                        try {
                            await deleteOneJobSchedule({
                                variables: { id: job.id },
                            });
                            deletedIdsRef.current.add(Number(job.id));
                        } catch (err) {
                            failed += 1;
                            starekContext.showError(err);
                        } finally {
                            setDeleteDone((d) => d + 1);
                        }
                    }
                    await hardRefresh();
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                } finally {
                    setIsDeleting(false);
                    closeConfirm();
                    onSettled?.(failed === 0);
                }
            },
            onClose: () => {
                closeConfirm();
                onSettled?.(false);
            },
        });
    };

    // Only the page's first load shows the full-page loader. A refetch or a view switch
    // keeps the page mounted (the operation bar holds the search box text), so the
    // sidebar and dialog are not torn down mid-action.
    if (activeInitialLoading && !hasLoaded) return <EntityLoadingState icon="fa-calendar" />;

    // Full-page error only before anything has loaded; later failures (page change,
    // search, view switch, refetch) keep the page and show an inline alert.
    return error && !hasLoaded ? (
        <div className="tw-text-center tw-mt-10 tw-text-red-600">
            Failed to load job schedules: {error.message}
        </div>
    ) : (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>
            {error && (
                <div className="alert alert-danger py-2 small">
                    Could not refresh job schedules: {error.message}
                </div>
            )}

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    // Merge, don't replace: replacing dropped ?page (and anything else).
                    setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set("view", mode);
                        return next;
                    });
                    setSelectedRows([]);
                }}
                onEdit={handleEdit}
                onDelete={() => handleDelete(selectedRows)}
            >
                <EntityOperationBar.View
                    mode="calendar"
                    icon="fa fa-calendar"
                    tooltip="Calendar View"
                >
                    <JobSchedulesCalendarView
                        jobSchedules={jobSchedules}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        onRefresh={hardRefresh}
                        onEditJob={handleEditJob}
                        filterText={filterText}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    <JobSchedulesListingView
                        items={pageItems}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        toggleCleared={toggleCleared}
                        loading={pageInitialLoading}
                        noDataComponent={<EntityEmptyState icon="fa-calendar" entityName="Job Schedule" messages={EMPTY_MESSAGES.jobSchedules} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                        onSort={setSort}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {isListView && (
                <Paginator
                    page={page}
                    pageCount={pageCount}
                    pageSize={pageSize}
                    total={total}
                    onChange={setPage}
                    disabled={pageLoading}
                />
            )}

            <JobScheduleSidebar
                show={showSidebar}
                onDiscard={handleSidebarClose}
                item={routeEditJob || selectedRows[0] || null}
                plantId={starekContext.lastUsedPlant.id}
                onSave={handleSaveJobSchedule}
                onOpenSaved={(saved) =>
                    navigate(buildListRoute(`/job-schedules/${saved.id}`, searchParams))
                }
                onDelete={(job) =>
                    new Promise((resolve) => handleDelete([job], resolve))
                }
                onProgressChange={hardRefresh}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { JobSchedules };
